import { APP_CACHE_TAG_CATEGORIES } from "@/lib/constants";
import { ApiResponseError } from "@/lib/error";
import { operations } from "@/openapi/generated";
import { api } from "@/openapi/client";
import { queryKeyToTag } from "@/lib/cache-utils";
import { categoryQueryKeys } from "@/app/[locale]/categories/_api/category-query-keys";

/**
 * Fetch paginated shops filtered by category id from the backend.
 */
export type ShopsByCategoryResponse = operations["getByCategory"]["responses"]["200"]["content"]["*/*"];

export async function getShopsByCategoryId(
    categoryId: string,
    page = 0,
    size = 20,
    client: typeof api = api
): Promise<ShopsByCategoryResponse> {
  const { data, error } = await client.GET("/shops/byCategory", {
    params: { query: { categoryId, page, size } },
    next: { revalidate: 36000, tags: [APP_CACHE_TAG_CATEGORIES] },
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getShopsByCategoryId");
  return data;
}

/**
 * Fetch the full, name-sorted category tree from the backend.
 */
export type CategoryTreeResponse = operations["getTree"]["responses"]["200"]["content"]["*/*"];
export type CategoryTreeNode = CategoryTreeResponse[number];

export async function getCategoryTree(
    client: typeof api = api
): Promise<CategoryTreeResponse> {
  const { data, error } = await client.GET("/categories/tree", {
    next: { tags: [queryKeyToTag(categoryQueryKeys.tree()), APP_CACHE_TAG_CATEGORIES] },
    cache: 'force-cache'
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getCategoryTree");
  return data;
}

/**
 * Find a category node in the tree by ID, returning the node and its ancestor chain.
 */
export function findCategoryInTree(
    tree: CategoryTreeResponse,
    id: string,
    ancestors: CategoryTreeNode[] = []
): { category: CategoryTreeNode; ancestors: CategoryTreeNode[] } | null {
    for (const node of tree) {
        if (node.id === id) {
            return { category: node, ancestors };
        }
        const found = findCategoryInTree(
            (node.subcategories ?? []) as CategoryTreeResponse,
            id,
            [...ancestors, node]
        );
        if (found) return found;
    }
    return null;
}

/**
 * Flatten all nodes in the category tree into a single array (all depths).
 */
export function flattenCategoryTree(tree: CategoryTreeResponse): CategoryTreeNode[] {
    const result: CategoryTreeNode[] = [];
    for (const node of tree) {
        result.push(node);
        if (node.subcategories?.length) {
            result.push(...flattenCategoryTree(node.subcategories as CategoryTreeResponse));
        }
    }
    return result;
}

/**
 * Fetch all category nodes (main + sub-categories) from the tree.
 */
export async function getAllCategories(
    client: typeof api = api
): Promise<CategoryTreeNode[]> {
    const tree = await getCategoryTree(client);
    return flattenCategoryTree(tree);
}

/**
 * Fetch main categories with shop counts from the backend.
 */
export type MainCategoriesWithCountsResponse = operations["getMainCategoriesWithCounts"]["responses"]["200"]["content"]["*/*"];

export async function getMainCategoriesWithCounts(
    client: typeof api = api
): Promise<MainCategoriesWithCountsResponse> {
  const { data, error } = await client.GET("/categories/main-with-counts", {
    next: { tags: [queryKeyToTag(categoryQueryKeys.mainWithCounts()), APP_CACHE_TAG_CATEGORIES] },
    cache: 'force-cache'
  });
  if (error) throw error;
  if (!data) throw new ApiResponseError("getMainCategoriesWithCounts");
  return data;
}